import { Search } from "lucide-react";
import { useEffect, useState } from "react";
import axios from "axios";
import { SectionCard } from "../../../Component/AdminComponents/SectionCard";
import RefreshButton from "../../../Component/Common/RefreshButton";
import RegularLoader from "../../../Component/Loader/RegularLoader";
import Pagination from "../../../Component/Common/Pagination";
import toast from "react-hot-toast";
import { BASE_URL, Warrenty_URL } from "../../../config/api";
import SimpleTable from "../../../Component/Common/SimpleTable";
import PriceTemplateViewModal from "./PriceTemplateViewModal";
import Badge from "../../../Component/Common/Badge";

export default function PriceTemplate() {
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [isViewOpen, setIsViewOpen] = useState(false);
const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // -------------------------
  // Fetch Price Templates
  // -------------------------
  const fetchTemplates = async (page = 1) => {
    try {
      setLoading(true);

      const response = await axios.get(
        `${BASE_URL}warranty/price-templates?page=${page}`
      );

      const list = response.data?.price_templates || [];

      setTemplates(list);
      setFilteredTemplates(list);

      // No pagination in API, so manually set
      setCurrentPage(1);
      setTotalPages(1);

    } catch (error) {
      console.log(error);
      toast.error("Failed to load price templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates(currentPage);
  }, [currentPage]);

  // -------------------------
  // Search Filter
  // -------------------------
  useEffect(() => {
    const filtered = templates.filter((t) =>
      t.warranty_product?.name
        ?.toLowerCase()
        .includes(debouncedSearch.toLowerCase())
    );

    setFilteredTemplates(filtered);
  }, [debouncedSearch, templates]);

  const templateRows = filteredTemplates.map((t) => ({
  raw: t,
  cells: [
    { title: t.warranty_product?.name || "—" },

    // 🔥 PLAN TYPE COLUMN
    {
      title:
        Number(t.warranty_product?.is_offer) === 1 ? (
          <Badge
            label="Subscription"
            color="blue"
          />
        ) : (
          <Badge
            label="Regular"
            color="green"
          />
        ),
    },

    { title: `₹${t.min_price} – ₹${t.max_price}` },

    {
      title:
        t.is_percent === 1
          ? `Emp ${t.emp_payout}% | Ret ${t.retailer_payout}%`
          : `Emp ₹${t.emp_payout} | Ret ₹${t.retailer_payout}`,
    },

    { title: t.warranty_product?.validity || "—" },

    {
      title: t.is_fixed === 1 ? "Fixed" : "Percent",
    },

    {
      title: new Date(t.created_at).toLocaleDateString(),
    },
  ],
}));

  return (
    <div className="space-y-4">
      <SectionCard title="Price Template List">

        {/* Search & Refresh */}
        <div className="flex justify-end gap-2 items-center mb-2">
          <div className="flex items-center gap-1 px-3 py-1 rounded-xl border border-slate-200 bg-slate-50">
            <Search className="w-4 h-4 text-slate-800" />
            <input
              type="text"
              placeholder="Search template..."
              className="bg-transparent text-xs outline-none placeholder:text-slate-700"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="hidden lg:block">
            <RefreshButton onClick={() => fetchTemplates(currentPage)} />
          </div>
        </div>

        {/* Loader */}
        {loading && (
          <div className="flex justify-center items-center h-[50vh]">
            <RegularLoader />
          </div>
        )}

        {/* No results */}
        {!loading && filteredTemplates.length === 0 && (
          <p className="text-center text-sm text-slate-500 py-3">
            No templates found.
          </p>
        )}

        {/* Table */}
        {!loading && filteredTemplates.length > 0 && (
        <>
          <div className="md:hidden space-y-4">
            {/* <div className="md:hidden px-3 py-4 space-y-6 bg-gradient-to-br from-slate-100 via-white to-indigo-100 rounded-3xl"> */}
              {filteredTemplates.map((t) => {
                const product = t.warranty_product;

                return (
                  <div
                    key={t.id}
                    className="bg-white rounded-3xl shadow-[0_20px_60px_rgba(0,0,0,0.08)] p-6 relative"
                  >
                    {/* Top Row */}
                    <div className="flex justify-between items-center">
                      <div className="h-12 w-12 rounded-2xl bg-white border border-slate-300 flex items-center justify-center shadow-sm overflow-hidden">
                        {product?.categories?.[0]?.image ? (
                          <img
                            src={product.categories[0].image}
                            alt={product.categories[0].name}
                            className="h-8 w-8 object-contain"
                          />
                        ) : (
                          <span className="text-indigo-500 text-lg">⚡</span>
                        )}
                      </div>

                      <span
                        className={`text-xs px-3 py-1 rounded-full border font-medium
                          ${
                            Number(product?.is_regular) === 1
                              ? "bg-emerald-50 text-emerald-600 border-emerald-200"
                              : Number(product?.is_offer) === 1
                              ? "bg-blue-50 text-blue-600 border-blue-200"
                              : "bg-slate-100 text-slate-500 border-slate-200"
                          }`}
                      >
                        {Number(product?.is_regular) === 1
                          ? "Regular"
                          : Number(product?.is_offer) === 1
                          ? "Offer"
                          : "Plan"}
                      </span>
                    </div>

                    {/* Title */}
                    <h3 className="mt-6 text-lg font-semibold text-slate-900">
                      {product?.name}
                    </h3>

                    <p className="text-sm text-slate-500 mt-1">
                      Valid for {product?.validity} days • {product?.claims} Claims
                    </p>

                    {/* Price */}
                    <div className="mt-5 flex items-end gap-2">
                      <span className="text-4xl font-bold text-slate-900">
                        ₹{product?.mrp}
                      </span>
                      <span className="text-sm text-slate-400 mb-1">
                        {t.is_fixed === 1 ? "/fixed" : "/percent"}
                      </span>
                    </div>

                    <div className="text-xs text-slate-400 mt-1">
                      Range ₹{t.min_price} – ₹{t.max_price}
                    </div>

                    {/* CTA */}
                    <button
                      onClick={() => {
                        setSelectedTemplate(t);
                        setIsViewOpen(true);
                      }}
                      className="mt-6 w-full py-3 rounded-2xl text-white font-medium text-sm 
                                bg-gradient-to-r from-blue-600 to-indigo-600
                                hover:opacity-90 transition shadow-md"
                    >
                      View Plan
                    </button>
                  </div>
                );
              })}
            {/* </div> */}
          </div>
          <div className="hidden md:block">
            <SimpleTable
              headers={[
                "Product",
                "Plan Type",
                "Price Range",
                "Payouts",
                "Validity",
                "Mode",
                "Created",
              ]}
              rows={templateRows}
              renderActions={(t) => (
                <button
                  onClick={() => {
                    setSelectedTemplate(t);
                    setIsViewOpen(true);
                  }}
                  className="bg-blue-100 text-blue-700 px-3 py-1 rounded-xl text-xs font-medium hover:bg-blue-200 transition"
                >
                  View
                </button>
              )}
              showSrNo
              pageSize={10}
            />
          </div>
          </>
        )}

        {/* Pagination (API has no pagination but keeping UI consistent) */}
        {!loading && filteredTemplates.length > 0 && totalPages > 1 && (
          <div className="mt-4 flex justify-center">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>
        )}

        
      </SectionCard>

      <PriceTemplateViewModal
        isOpen={isViewOpen}
        onClose={() => {
          setIsViewOpen(false);
          setSelectedTemplate(null);
        }}
        template={selectedTemplate}
      />
    </div>
  );
}
